import { useState, useEffect } from 'react';
import { Seo, usePageSeo, assetUrl, absUrl } from '../lib/seo';
import { PageSeoContent } from '../components/common/RichContent';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Award, Phone, BadgeCheck, ChevronLeft, Building2, GraduationCap, ImageIcon, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { doctorsAPI, reviewsAPI } from '../lib/api';
import { getWhatsAppLink, getCategoryColor, trackWhatsAppClick } from '../lib/utils';
import { ReviewWidget } from '../components/common/ReviewWidget';
import { LogoLoader } from '../components/common/LogoLoader';

export const DoctorProfilePage = () => {
  const seo = usePageSeo();
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [reviews, setReviews] = useState({ reviews: [], total_count: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const response = await doctorsAPI.getBySlug(slug);
        setDoctor(response.data);
        
        // Fetch reviews for this doctor
        if (response.data?.id) {
          try {
            const reviewsRes = await reviewsAPI.getByDoctor(response.data.id);
            setReviews(reviewsRes.data);
          } catch (e) {
            console.log('No reviews yet');
          }
        }
      } catch (error) {
        console.error('Error fetching doctor:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctor();
  }, [slug]);

  const handleWhatsAppClick = () => {
    if (doctor) {
      trackWhatsAppClick(`doctor-profile-${doctor.slug}`, doctor.id, doctor.name);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-alabaster">
        <LogoLoader label="Loading doctor profile..." className="min-h-[70vh]" />
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-gray-700 mb-4">Doctor not found</h2>
          <Link to="/doctors">
            <Button>Browse All Doctors</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-alabaster" data-testid="doctor-profile-page">
      <Seo override={seo} page={{ meta_title: doctor.meta_title, meta_description: doctor.meta_description }}
        title={`${doctor.name} - Dermatologist${doctor.location?.city ? ` in ${doctor.location.city}` : ''} | DrPortals`}
        description={(doctor.bio || `${doctor.name}, ${doctor.experience_years}+ years experience at ${doctor.clinic_name}.`).slice(0, 160)}
        image={doctor.photo_url} type="profile"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Physician", "name": doctor.name, "image": doctor.photo_url, "description": doctor.bio,
          "url": absUrl(`/doctor/${doctor.slug}`),
          "medicalSpecialty": doctor.speciality_details?.map(s => s.name).join(', '),
          ...(reviews.total_count > 0 && { "aggregateRating": { "@type": "AggregateRating", "ratingValue": reviews.average_rating, "reviewCount": reviews.total_count } }),
          "address": doctor.clinic_address ? { "@type": "PostalAddress", "streetAddress": doctor.clinic_address, "addressLocality": doctor.location?.city, "addressCountry": doctor.location?.country } : undefined
        }} />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link to="/doctors" className="inline-flex items-center text-sm text-gray-600 hover:text-navy-800">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Doctors
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header Section */}
          <div className="md:flex">
            {/* Photo */}
            <div className="md:w-1/3 relative">
              <div className="aspect-square md:h-full">
                <img
                  src={doctor.photo_url || 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=600'}
                  alt={doctor.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {doctor.is_verified && (
                <div className="absolute top-4 left-4 verified-badge flex items-center gap-1.5">
                  <BadgeCheck className="w-4 h-4" />
                  Verified Doctor
                </div>
              )}
            </div>

            {/* Info */}
            <div className="md:w-2/3 p-6 md:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900 mb-2" data-testid="doctor-name">
                    {seo.h1 || doctor.name}
                  </h1>
                  
                  {/* Specialities */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {doctor.speciality_details?.map((spec) => (
                      <Badge
                        key={spec.id}
                        className={`${getCategoryColor(spec.category).bg} ${getCategoryColor(spec.category).text}`}
                      >
                        {spec.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                {doctor.consultation_fee && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Consultation Fee</p>
                    <p className="font-heading text-xl font-bold text-navy-800">{doctor.consultation_fee}</p>
                  </div>
                )}
              </div>

              {/* Quick Info */}
              <div className="flex flex-wrap gap-6 mt-4 mb-6">
                {doctor.location && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <span>{doctor.location.city}, {doctor.location.country}</span>
                  </div>
                )}
                {doctor.experience_years > 0 && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span>{doctor.experience_years} years experience</span>
                  </div>
                )}
              </div>

              {/* Clinic */}
              {doctor.clinic_name && (
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl mb-6">
                  <Building2 className="w-5 h-5 text-navy-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-navy-900">{doctor.clinic_name}</p>
                    {doctor.clinic_address && (
                      <p className="text-sm text-gray-600 mt-1">{doctor.clinic_address}</p>
                    )}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={getWhatsAppLink(`Hi, I'd like to book an appointment with ${doctor.name}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  onClick={handleWhatsAppClick}
                  data-testid="whatsapp-cta"
                >
                  <Button className="w-full rounded-full bg-whatsapp hover:bg-whatsapp/90 text-white py-6 gap-2 text-base">
                    <Phone className="w-5 h-5" />
                    Book via WhatsApp
                  </Button>
                </a>
                <Button variant="outline" className="rounded-full border-navy-800 text-navy-800 hover:bg-navy-800 hover:text-white py-6">
                  Share Profile
                </Button>
              </div>
              
              {/* Rating Summary */}
              {reviews.total_count > 0 && (
                <div className="flex items-center gap-4 mt-4 p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-5 h-5 ${i <= Math.round(reviews.average_rating) ? 'text-amber-400 fill-current' : 'text-gray-200'}`} />
                    ))}
                  </div>
                  <span className="font-semibold text-gray-800">{reviews.average_rating}</span>
                  <span className="text-gray-500 text-sm">({reviews.total_count} reviews)</span>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* About Section */}
          <div className="p-6 md:p-8">
            <h2 className="font-heading text-xl font-semibold text-navy-900 mb-4">About</h2>
            <p className="text-gray-600 leading-relaxed">
              {doctor.bio || 'No bio available.'}
            </p>
          </div>

          {/* Certifications & Awards */}
          {(doctor.certifications?.length > 0 || doctor.awards?.length > 0) && (
            <>
              <Separator />
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {doctor.certifications?.length > 0 && (
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-gold" />
                        Certifications
                      </h2>
                      <ul className="space-y-2">
                        {doctor.certifications.map((cert, index) => (
                          <li key={index} className="flex items-center gap-2 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                            {cert}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {doctor.awards?.length > 0 && (
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-gold" />
                        Awards
                      </h2>
                      <ul className="space-y-2">
                        {doctor.awards.map((award, index) => (
                          <li key={index} className="flex items-center gap-2 text-gray-600">
                            <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                            {award}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Gallery */}
          {doctor.gallery_images?.length > 0 && (
            <>
              <Separator />
              <div className="p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-navy-900 mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-gold" />
                  Gallery
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {doctor.gallery_images.map(assetUrl).map((img, index) => (
                    <Dialog key={index}>
                      <DialogTrigger asChild>
                        <button className="aspect-video rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
                          <img src={img} alt={`${doctor.name} clinic gallery photo ${index + 1}`} loading="lazy" className="w-full h-full object-cover" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl p-0 overflow-hidden">
                        <img src={img} alt={`${doctor.name} clinic gallery photo ${index + 1}`} className="w-full h-auto" />
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Patient Reviews */}
          {reviews.reviews?.length > 0 && (
            <>
              <Separator />
              <div className="p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-navy-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-gold" />
                  Patient Reviews ({reviews.total_count})
                </h2>
                <div className="space-y-4">
                  {reviews.reviews.map((review) => (
                    <div key={review.id} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-medium text-gray-900">{review.patient_name}</span>
                          {review.treatment && (
                            <span className="ml-2 text-xs text-gray-500 bg-white px-2 py-0.5 rounded">
                              {review.treatment}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'text-amber-400 fill-current' : 'text-gray-200'}`} />
                          ))}
                        </div>
                      </div>
                      <h4 className="font-medium text-gray-800 mb-1">{review.title}</h4>
                      <p className="text-gray-600 text-sm">{review.review}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Review Widget - Outside main card */}
        <div className="mt-6">
          <ReviewWidget doctorId={doctor.id} doctorName={doctor.name} />
        </div>

      </div>
      <PageSeoContent seo={seo} />
    </div>
  );
};
